<template>
  <div class="bg-white pb-4 min-h-screen">
    <div v-if="!topPage" class="fixed bottom-4 right-4 cursor-pointer hidden md:block" @click.prevent="scrollToTop">
      <ArrowUpCircleIcon
        class="h-5 md:h-7 lg:h-7 w-5 md:w-7 lg:w-7 inline-block text-pdsPrimaryColor cursor-pointer"
        aria-hidden="true"
      />
    </div>
    <div class="text-white font-bold p-0 bg-primary-default">
      <div class="ml-auto w-11/12">
        <div class="grid grid-rows md:grid-cols md:grid-rows-1 md:grid-cols-2">
          <div class="py-4">
            <!-- Back Link -->
            <div class="text-sm mb-1 cursor-pointer -ml-1">
              <router-link to="/">
                <ChevronLeftIcon class="h-5 w-5 inline-block" aria-hidden="true" />
                <span class="align-middle">Patiënt zoeken</span>
              </router-link>
            </div>
            <h1 class="text-2xl font-bold tracking-tight text-white m-0 pl-1">{{ computedPageTitle }}</h1>
          </div>
        </div>
      </div>
    </div>
    <section class="mypatients pt-6 mx-auto w-10/12">
      <div class="grid grid-rows">
        <div class="relative mb-2 w-full md:w-1/2">
          <div class="text-pdsPrimaryColor mb-3 text-base font-bold">Zoek patiënt</div>
          <v-input-group-simple-2
            id="Searchbar"
            v-model="query"
            placeholder="Zoek op naam, BSN, geboortedatum, postcode, polisnummer of patiëntnummer"
            @keyup="onKeyUp"
          />
          <sync-loader v-if="loading" color="#73c8d2" size="8px" class="absolute right-4 top-12" />
          <MagnifyingGlassIcon v-else class="w-5 h-5 text-pdsPrimaryColor ml-2 absolute right-4 top-12" />
        </div>
        <div class="my-2 text-pdsPrimaryColor" v-if="searchResults && searchResults.patients.length > 0">
          Scroll omlaag om meer patiënten te laden die actief zijn binnen jouw afdeling.
        </div>
      </div>
      <div v-if="searchResults && searchResults.patients.length == 0 && !loading" class="fade-element">
        <div>
          <h1 class="text-2xl font-bold tracking-tight text-primary-default my-4">Sorry, geen resultaten gevonden</h1>
          <h4 class="text-pdsPrimaryColor mb-4">
            De zoekopdracht gaf geen patiënten binnen uw afdeling. Zoek hieronder op bestaande patiënten buiten uw
            afdeling of maak een nieuwe patiënt aan.
          </h4>
          <v-search :is-sub-search="true"></v-search>
        </div>
      </div>
      <div v-if="searchResults && searchResults.patients.length > 0">
        <div class="">
          <v-listing-item
            v-for="(patient, index) in searchResults.patients"
            :key="patient.PatientId || index"
            :patient="patient"
            @query="queryForPatients"
            @select="selectPatient"
          />
          <div v-if="isMobile && hideLoadMoreButton" class="pt-4 flex justify-center">
            <button @click="loadMoreData" class="block sm:hidden bg-pdsPrimaryColor text-white py-2 px-2 rounded-full">
              Laad meer patiënten
            </button>
          </div>
          <div v-if="hasError === true" class="my-4 text-pdsPrimaryColor text-base">Er is een fout opgetreden.</div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import router from '@/router';
import VListingItem from '@/components/sections/mypatients/MyPatientsListingItem';
import vSearch from '@/components/sections/search/Search';
import SyncLoader from 'vue-spinner/src/SyncLoader.vue';
import { computed, ref, onBeforeMount, onUnmounted, watch } from 'vue';
import { useStore, mapActions } from 'vuex';
import { ChevronLeftIcon, ArrowUpCircleIcon } from '@heroicons/vue/20/solid';
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline';
import { Subject, from } from 'rxjs';
import { switchMap, refCount, map, concatMap, startWith, scan, publishBehavior, tap, catchError } from 'rxjs/operators';
import axios from 'axios';

export default {
  name: 'MyPatientsListing',
  components: {
    ChevronLeftIcon,
    MagnifyingGlassIcon,
    VListingItem,
    SyncLoader,
    vSearch,
    ArrowUpCircleIcon
  },
  beforeRouteEnter(to, from, next) {
    next();
  },
  beforeRouteUpdate(to, from, next) {
    next();
  },
  setup() {
    const store = useStore();
    const statePatientId = store.state.patient.patientGegevens.PatientId;
    const setEndpoints = (value) => store.dispatch('patient/setEndpoints', value);
    const setActivePatient = (value) => store.dispatch('patient/setActivePatient', value);
    const topPage = ref(true);
    const bottom = ref(false);
    const query = ref('');
    const typingTimer = ref(null);
    const loading = ref(null);
    const page = ref(0);
    const searchResults = ref(null);
    const loadingSubject$ = new Subject();
    const patientsRequestSubject$ = new Subject();
    const patientsLoadMoreSubject$ = new Subject();
    const hasError = ref(null);
    const isMobile = ref(window.innerWidth < 640);

    const computedPageTitle = computed(() => {
      return router.currentRoute.value.name === 'mijnpatienten' ? 'Mijn patiënten' : 'Patiënten van afdeling';
    });
    const hideLoadMoreButton = computed(() => {
      const patients = searchResults?.value?.patients ?? [];
      const total = searchResults?.value?.totalResult ?? 0;
      return patients.length > 0 && patients.length < total;
    });

    const topVisible = () => {
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      return scrollY === 0;
    };
    const bottomVisible = () => {
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      const visible = document.documentElement.clientHeight;
      const pageHeight = document.documentElement.scrollHeight;
      const bottomOfPage = Math.ceil(visible + scrollY) >= Math.ceil(pageHeight);
      return bottomOfPage;
    };
    const scrollToTop = () => {
      window.scrollTo(0, 0);
    };
    const handleResize = () => {
      isMobile.value = window.innerWidth < 640;
    };
    const selectPatient = (PatientId) => {
      setActivePatient(PatientId).then((result) => {
        if (result.data.IsActiveCustomer) {
          router.push('/patientdashboard');
        } else {
          setEndpoints('edit');
          router.push('/patientgegevens');
        }
      });
    };
    const queryForPatients = () => {
      let apiCall = '/MyPatients/PatientSearch';
      patientsRequestSubject$.next(apiCall);
      patientsLoadMoreSubject$.next(true);
    };
    const onKeyUp = () => {
      clearTimeout(typingTimer.value);
      page.value = 1;
      typingTimer.value = setTimeout(() => queryForPatients(), 600);
    };
    const searchResults$ = patientsRequestSubject$.pipe(
      switchMap((request) =>
        patientsLoadMoreSubject$.pipe(
          tap(() => {
            loadingSubject$.next(true);
          }),
          concatMap((_, pageNum) => {
            let params = {
              query: query.value,
              page: pageNum + 1
            };

            if (router.currentRoute.value.name !== 'mijnpatienten') {
              params.isDepartment = true;
            }
            return from(axios.get(request, { params: params })).pipe(
              catchError(() => {
                return Promise.resolve({ data: { totalResult: -1, error: true, patients: [] } });
              }),
              map((r) => r.data)
            );
          }),
          scan(
            (previous, p) => {
              if (!p) {
                return previous;
              }
              return {
                totalResult: p.totalResult,
                error: p.error,
                patients: [...previous.patients, ...p.patients.map((patient) => Object.assign(patient))]
              };
            },
            { totalResult: 0, patients: [] }
          ),
          tap(() => loadingSubject$.next(false)),
          publishBehavior({
            totalResult: -1,
            error: false,
            patients: []
          }),
          refCount()
        )
      )
    );
    const addScroll = () => {
      window.addEventListener('scroll', () => {
        bottom.value = bottomVisible();
        topPage.value = topVisible();
      });
    };
    const removeScroll = () => {
      window.removeEventListener('scroll', () => {
        bottom.value = bottomVisible();
        topPage.value = topVisible();
      });
    };
    const loadMoreData = () => {
      patientsLoadMoreSubject$.next(true);
    };

    watch(
      () => bottom.value,
      (newValue, oldValue) => {
        if (newValue) {
          patientsLoadMoreSubject$.next(true);
        }
      }
    );
    watch(isMobile, (mobile) => {
      if (mobile) {
        removeScroll();
      } else {
        addScroll();
      }
    });

    onBeforeMount(() => {
      window.addEventListener('resize', handleResize);
      if (!isMobile.value) {
        addScroll();
      }
      queryForPatients();
    });

    onUnmounted(() => {
      searchResults.value = { totalResult: 0, patients: [] };
    });

    loadingSubject$.asObservable().subscribe((value) => (loading.value = value));
    searchResults$.subscribe((r) => {
      searchResults.value = {
        totalResult: r.totalResult,
        patients: r.patients
      };
      hasError.value = r.error;
    });

    return {
      statePatientId,
      setEndpoints,
      ...mapActions('patient', ['setActivePatient']),
      setActivePatient,
      topVisible,
      bottomVisible,
      scrollToTop,
      computedPageTitle,
      topPage,
      query,
      typingTimer,
      loading,
      selectPatient,
      queryForPatients,
      onKeyUp,
      searchResults,
      patientsRequestSubject$,
      patientsLoadMoreSubject$,
      isMobile,
      loadMoreData,
      hideLoadMoreButton
    };
  }
};
</script>
